import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import SEO from "@/components/SEO";
import LeadDialog from "@/components/LeadDialog";
import ChecklistOptin from "@/components/ChecklistOptin";
import DisasterQuoteCTA from "@/components/DisasterQuoteCTA";
import DisasterRibbon from "@/components/DisasterRibbon";
import Breadcrumbs from "@/components/Breadcrumbs";
import SyndicateBlock from "@/components/SyndicateBlock";
import Byline from "@/components/Byline";
import HeroImg from "@/components/HeroImg";
import StickyDecisionCalcBanner, { STICKY_BANNER_GUIDE_SLUGS, STICKY_BANNER_CTA_BY_SLUG } from "@/components/StickyDecisionCalcBanner";
import { ArrowRight, ChevronLeft, Clock4, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GUIDES, getGuide, getRelatedGuides, getSameStateGuides } from "@/lib/guides";
import { findRedirect } from "@/lib/redirects";
import { withUtm } from "@/lib/utm";
import { CALCULATORS, getStateLandingUrl } from "@/lib/calculators";
import { getStateByCode } from "@/lib/states";
import { getStateCostIndex, getStateProfile } from "@/lib/stateDrivers";
import { breadcrumbSchema, guideArticleSchema } from "@/lib/schema";
import { bylineForTopic, AUTHORS } from "@/lib/editorial";
import NotFoundPage from "@/pages/NotFoundPage";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

/**
 * Try to recover from a near-miss slug (e.g., "/guides/hiring-a-contractor" -> "hiring-a-contractor-checklist").
 * Returns the canonical slug if a single confident match is found, otherwise null.
 */
function fuzzyMatchSlug(slug) {
  if (!slug) return null;
  const matches = GUIDES.filter((g) => {
    const a = g.slug;
    const b = slug;
    return a.startsWith(`${b}-`) || b.startsWith(`${a}-`) || a.includes(b) || b.includes(a);
  });
  return matches.length === 1 ? matches[0].slug : null;
}

export default function GuidePage() {
  const { slug } = useParams();
  const guide = getGuide(slug);
  const [leadOpen, setLeadOpen] = useState(false);
  // Lets a guide Body open the lead dialog mid-article with a custom source tag
  // (e.g. "finance-guide-mid-article") for clean attribution in admin/CSV exports.
  const [leadSource, setLeadSource] = useState(null);
  const openLead = (source) => {
    setLeadSource(source || null);
    setLeadOpen(true);
  };

  // 2026-09-29 pass 92u — Lazy-load the guide's Body + extraSchema.
  // guides.js is now metadata-only (797 eager imports were bloating the
  // main bundle to 1.43MB gzipped). The visited guide's module is fetched
  // as its own webpack chunk on demand; metadata (h1, description, byline,
  // hero, schema) renders instantly from the meta index. Prerendered build
  // HTML (scripts/prerender-bodies.js) is unaffected — crawlers still get
  // full content.
  const [Body, setBody] = useState(null);
  const [extraSchemas, setExtraSchemas] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setBody(null);
    setExtraSchemas([]);
    if (guide?.slug) {
      import(`@/guides/${guide.slug}.jsx`)
        .then((m) => {
          if (cancelled) return;
          const g = m.default || {};
          if (g.Body) setBody(() => g.Body);
          if (Array.isArray(g.extraSchema)) setExtraSchemas(g.extraSchema);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide?.slug]);

  if (!guide) {
    // Consult the static + pattern redirect tables BEFORE running the
    // loose fuzzyMatchSlug() heuristic. STATIC_PATH_REDIRECTS entries for
    // /guides/{slug} are explicit and curated — they MUST win over the
    // fuzzy matcher's .includes()-based scoring, which can otherwise pick
    // a coincidentally-substring-overlapping sibling guide as a false-
    // positive (caught by testing_agent iteration_51 on
    // /guides/how-much-does-a-basement-addition-cost-2026).
    // Passing `null` as the redirects param skips the admin-DB lookup
    // here (NotFoundPage still does the full lookup if we fall through),
    // keeping this synchronous and free on every guide page mount.
    const target = findRedirect(`/guides/${slug}`, null);
    if (target) return <Navigate to={target} replace />;
    const recovered = fuzzyMatchSlug(slug);
    if (recovered) return <Navigate to={`/guides/${recovered}`} replace />;
    return <NotFoundPage />;
  }

  const path = `/guides/${guide.slug}`;
  const relatedGuides = getRelatedGuides(guide.slug);
  const relatedCalcs = (guide.related || []).map((s) => CALCULATORS[s]).filter(Boolean);
  // Sibling state guides — every OTHER project's deep-dive for the SAME
  // state as this guide. Surfaces auto-discovered, ensures every single-state
  // guide gets 5+ inbound internal links from other pages on the site.
  const sameStateGuides = getSameStateGuides(guide);

  // Cross-product of related calculators × related states → state-specific landing pages.
  // Each link uses the calculator + state's slug (e.g. /bathroom-remodel-cost-california).
  const relatedStateLinks = (guide.relatedStates || []).flatMap((code) => {
    const state = getStateByCode(code);
    if (!state) return [];
    return relatedCalcs.map((c) => ({
      key: `${c.slug}-${state.code}`,
      to: getStateLandingUrl(c, state.slug),
      label: `${c.shortName} cost in ${state.name}`,
      stateCode: state.code,
    }));
  });

  // "Why is {state} expensive?" callout — surfaces the cost-driver deep-dive
  // page prominently when the current guide is a single-state deep-dive
  // (e.g. bathroom-remodel-cost-california-2026 with relatedStates: ["CA"]).
  // Single-state guides are the readers most likely to convert on a "why is
  // California expensive" follow-up — they've already committed to the state
  // angle. Multi-state and national guides intentionally skip this to keep
  // the article footer focused.
  const focalState = (() => {
    if (!Array.isArray(guide.relatedStates) || guide.relatedStates.length !== 1) return null;
    const s = getStateByCode(guide.relatedStates[0]);
    if (!s) return null;
    const idx = getStateCostIndex(s.code);
    const profile = getStateProfile(s.code);
    const pct = Math.round((idx - 1) * 100);
    return {
      ...s,
      idx,
      pct,
      headline: profile?.headline || null,
      isAbove: pct > 2,
      isBelow: pct < -2,
    };
  })();

  // Use the build-generated branded Open Graph card at /og/{slug}.jpg when
  // available — gives every shared link a polished, on-brand preview.
  // Falls back gracefully to the raw hero on the off-chance the build script
  // didn't produce a card for this slug.
  const ogCardUrl = `/og/${guide.slug}.jpg`;

  // Detect auto-generated per-state guides vs hand-written editorial guides.
  // Auto-gen state guides have a single relatedStates entry + a slug pattern
  // like `{project}-cost-{state}-2026` produced by the multi-script. These
  // should NOT claim a named Person author — they're templated. Hand-written
  // editorial guides (smart-home cluster, ROI playbook, hiring contractor,
  // etc.) get the full Person author + reviewedBy treatment, which is what
  // drives Perplexity / ChatGPT citation rates up.
  const isAutoGenStateGuide =
    Array.isArray(guide.relatedStates) &&
    guide.relatedStates.length === 1 &&
    /-cost-[a-z-]+-20\d{2}$/.test(guide.slug);

  const structuredData = [
    guideArticleSchema(guide, { ogCardUrl, isAutoGenStateGuide }),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Guides", path: "/guides" },
      { name: guide.title, path },
    ]),
    // Guide-level extras (e.g. cost-trajectory Dataset schema baked in by the
    // multi-state generator). Lazy-loaded with the guide's chunk (pass 92u) —
    // filter out nulls so empty/invalid blocks drop out cleanly.
    ...(Array.isArray(extraSchemas) ? extraSchemas.filter(Boolean) : []),
  ].filter(Boolean);

  const breadcrumbItems = [
    { name: "Home", path: "/" },
    { name: "Guides", path: "/guides" },
    { name: guide.title, path },
  ];

  return (
    <>
      <SEO
        title={`${guide.title} | HavenCostGuide`}
        description={guide.description}
        path={path}
        structuredData={structuredData}
        image={ogCardUrl}
        imageWidth={1200}
        imageHeight={630}
        type="article"
        publishedTime={guide.date}
        modifiedTime={guide.dateModified || guide.date}
        author="HavenCostGuide"
        section={guide.category || "Home Renovation Costs"}
        tags={[
          guide.category,
          ...(guide.relatedStates || []).map((c) => `${c} renovation cost`),
          ...(guide.related || []).map((r) => `${r} cost`),
          "2026 renovation costs",
        ].filter(Boolean)}
      />

      <div className="border-b border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-3xl px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/guides"
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
            data-testid="back-to-guides-link"
          >
            <ChevronLeft className="h-4 w-4" /> All guides
          </Link>
        </div>
      </div>

      <article className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        {guide.category === "Disaster" && (
          <DisasterRibbon
            stateCode={Array.isArray(guide.relatedStates) && guide.relatedStates.length === 1 ? guide.relatedStates[0] : null}
          />
        )}
        <Breadcrumbs items={breadcrumbItems} />
        {guide.category === "Disaster" && (
          <Link
            to="/storm-damage-claim-calculator"
            className="mt-4 flex items-center justify-between rounded-xl border border-emerald-300 bg-emerald-50/60 px-4 py-3 text-sm transition hover:border-emerald-500 hover:bg-emerald-50"
            data-testid="disaster-guide-calc-link"
          >
            <span className="font-medium text-slate-800">📊 Not sure if you should file? <strong className="text-emerald-800">Free claim decision calculator</strong> — ACV vs RCV math + 5-year premium impact</span>
            <span className="ml-3 text-emerald-700">→</span>
          </Link>
        )}
        <header className="mt-6 mb-10">
          <p className="text-xs font-medium uppercase tracking-wider text-green-700">{guide.category}</p>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl" data-testid="guide-h1">
            {guide.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            <span>{fmtDate(guide.date)}</span>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock4 className="h-3.5 w-3.5" />
              {guide.readingMinutes} min read
            </span>
          </div>
          {/* Named-human byline — surfaces accountable authors for AdSense /
              E-E-A-T evaluation. Auto-generated per-state guides skip the
              full byline card (they're templated cost data, not editorial)
              and instead show a compact "Reviewed by" line under the date. */}
          {(() => {
            // Topic-route the byline so utility-rebate / energy guides get
            // Riley first, cost / permits / insurance get Jordan first.
            const isEnergyTopic = /energy|rebate|heat-pump|hvac|insulation|furnace|solar|heehra/i.test(guide.slug);
            const isDisasterTopic = guide.category === "Disaster";
            const topic = isEnergyTopic ? "energy" : isDisasterTopic ? "insurance" : "cost";
            const b = bylineForTopic(topic);
            if (isAutoGenStateGuide) {
              return (
                <Byline
                  author={b.author}
                  reviewer={b.reviewer}
                  dateLabel={`updated ${fmtDate(guide.dateModified || guide.date)}`}
                  dateISO={guide.dateModified || guide.date}
                  compact
                  testid="guide-byline-compact"
                />
              );
            }
            return (
              <Byline
                author={b.author}
                reviewer={b.reviewer}
                dateLabel={fmtDate(guide.dateModified || guide.date)}
                dateISO={guide.dateModified || guide.date}
                testid="guide-byline"
              />
            );
          })()}
        </header>

        {guide.heroImage && (
          <div className="mb-10 aspect-[16/9] overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <HeroImg
              src={guide.heroImage}
              alt={guide.title}
              className="h-full w-full object-cover"
              priority
              width={1600}
              height={900}
            />
          </div>
        )}

        {/* Prominent in-article calculator CTA — sits at the top of the
            article (above the fold on most readers' scroll position) so the
            primary conversion CTA is visible without reading the whole guide.
            Uses the FIRST related calculator (the canonical pairing) — falls
            back to /calculators index if the guide doesn't have a primary
            related calc (e.g., cross-topic ROI hub). The bottom-of-article
            "Run the numbers" CTA still ships for readers who scroll through
            — the two work together rather than replacing each other. */}
        {(() => {
          const primaryCalc = relatedCalcs[0];
          if (!primaryCalc) return null;
          return (
            <aside
              className="mb-10 flex flex-col items-start gap-4 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-emerald-50/40 to-white p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
              data-testid="guide-inline-calc-cta"
            >
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                  Skip the article — get your number
                </p>
                <p className="mt-1 font-display text-base font-semibold text-slate-900 sm:text-lg">
                  Get a state-adjusted {primaryCalc.shortName.toLowerCase()} estimate in 60 seconds
                </p>
              </div>
              <Link
                to={withUtm(primaryCalc.path, {
                  source: "guide",
                  medium: "inline-cta",
                  campaign: guide.slug,
                })}
                className="group inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 hover:shadow-md"
                data-testid="guide-inline-calc-cta-link"
              >
                Open {primaryCalc.shortName.toLowerCase()} calculator
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </aside>
          );
        })()}

        <div className="guide-prose" data-testid="guide-body">
          {Body ? <Body onOpenLead={openLead} /> : <GuideBodySkeleton />}
        </div>

        {/* Auto-inject the high-conversion "3 free quotes" CTA at the bottom
            of every Disaster-category guide. Storm-damage searchers convert
            at 8-15% to quote requests (vs. 1-3% for general info), so this
            placement turns 11 SEO-only guides into a 11-port lead pipeline. */}
        {guide.category === "Disaster" && (
          <DisasterQuoteCTA
            projectType={
              guide.slug.includes("wildfire") ? "Wildfire Home Rebuild" :
              guide.slug.includes("flood") ? "Flood Damage Repair" :
              guide.slug.includes("tornado") ? "Tornado Damage Repair" :
              guide.slug.includes("hurricane") ? "Hurricane Damage Repair" :
              "Storm Damage Roof Replacement"
            }
            source={`damage-guide-bottom-${guide.slug}`}
          />
        )}

        {/* Optional email opt-in — surfaces on guides that opt-in via `optin` */}
        {guide.optin && (
          <ChecklistOptin
            title={guide.optin.title}
            description={guide.optin.description}
            benefit={guide.optin.benefit}
            stickyLabel={guide.optin.stickyLabel}
            source={guide.optin.source || `guide-optin-${guide.slug}`}
            notes={guide.optin.notes || `Requested asset for guide: ${guide.title}`}
          />
        )}

        {/* "Why is {state} expensive?" callout — only on single-state guides.
            Surfaces the matching cost-driver deep-dive page as a styled card
            instead of relying on inline anchors inside the article body. Tone
            adapts to whether the state is above/below/near the national avg. */}
        {focalState && (
          <aside
            className={`mt-14 rounded-2xl border p-6 sm:p-8 ${
              focalState.isAbove
                ? "border-rose-200 bg-rose-50"
                : focalState.isBelow
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-slate-200 bg-slate-50"
            }`}
            data-testid="guide-why-state-callout"
          >
            <div className="flex items-start gap-4 sm:items-center">
              <div
                className={`hidden h-12 w-12 shrink-0 items-center justify-center rounded-full sm:flex ${
                  focalState.isAbove
                    ? "bg-rose-100 text-rose-700"
                    : focalState.isBelow
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-200 text-slate-700"
                }`}
              >
                {focalState.isAbove ? (
                  <TrendingUp className="h-5 w-5" />
                ) : focalState.isBelow ? (
                  <TrendingDown className="h-5 w-5" />
                ) : (
                  <Minus className="h-5 w-5" />
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-xs font-semibold uppercase tracking-wider ${
                    focalState.isAbove
                      ? "text-rose-700"
                      : focalState.isBelow
                        ? "text-emerald-700"
                        : "text-slate-700"
                  }`}
                >
                  {focalState.pct > 0 ? "+" : ""}
                  {focalState.pct}% vs. national average
                </p>
                <h2 className="mt-1 font-display text-xl font-semibold text-slate-900">
                  Why is {focalState.name} renovation expensive?
                </h2>
                {focalState.headline && (
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">
                    {focalState.headline}
                  </p>
                )}
                <Link
                  to={`/why-is-${focalState.slug}-renovation-expensive`}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 transition hover:text-green-700"
                  data-testid="guide-why-state-link"
                >
                  See the full {focalState.name} cost-driver breakdown
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </aside>
        )}

        {/* Same-state sibling guides — every OTHER state-specific guide for
            the current state. Auto-discovered from the `relatedStates: ["XX"]`
            tag on each guide. Boosts inbound internal link count on every
            programmatic state page (was ~1 inbound; now 5-6 inbound) which
            was the root cause of "Crawled - not indexed" GSC signals on
            programmatic state-specific guides. */}
        {sameStateGuides.length > 0 && focalState && (
          <section className="mt-14" data-testid="guide-same-state-guides">
            <h2 className="font-display text-xl font-semibold text-slate-900">
              More cost guides for {focalState.name}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Planning multiple projects? Every other 2026 {focalState.name} cost guide carries the same state-specific labor and pricing detail.
            </p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {sameStateGuides.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/guides/${g.slug}`}
                    className="group flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 transition hover:border-emerald-400 hover:shadow-sm"
                    data-testid={`guide-same-state-${g.slug}`}
                  >
                    <span className="flex flex-col">
                      <span className="text-xs font-medium uppercase tracking-wider text-emerald-700">{g.category}</span>
                      <span className="mt-1 font-display text-sm font-semibold leading-snug text-slate-900">{g.title}</span>
                    </span>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-emerald-600" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Related calculators CTA */}
        {relatedCalcs.length > 0 && (
          <aside className="mt-14 rounded-2xl border border-slate-200 bg-slate-900 p-6 text-white sm:p-8" data-testid="guide-related-calcs">
            <h2 className="font-display text-xl font-semibold">Run the numbers for your project</h2>
            <p className="mt-2 text-sm text-slate-300">
              Get a state-adjusted cost estimate in 60 seconds.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {relatedCalcs.map((c) => (
                <Link
                  key={c.slug}
                  to={withUtm(c.path, {
                    source: "guide",
                    medium: "bottom-cta",
                    campaign: guide.slug,
                  })}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-900 transition hover:bg-slate-100"
                  data-testid={`guide-related-calc-${c.slug}`}
                >
                  {c.shortName} calculator
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              ))}
              <Button
                onClick={() => setLeadOpen(true)}
                className="rounded-full bg-green-600 px-5 text-sm text-white hover:bg-green-700"
                data-testid="guide-get-quotes"
              >
                Get free quotes
              </Button>
            </div>
          </aside>
        )}

        {/* Related state pages — auto-cross-product of guide.related × guide.relatedStates */}
        {relatedStateLinks.length > 0 && (
          <section className="mt-14" data-testid="guide-related-states">
            <h2 className="font-display text-xl font-semibold text-slate-900">
              Cost by state for this project
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              State-adjusted ranges with local labor and material multipliers.
            </p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {relatedStateLinks.map((l) => (
                <li key={l.key}>
                  <Link
                    to={l.to}
                    className="inline-flex items-center gap-1 text-sm text-slate-700 underline-offset-4 hover:text-green-700 hover:underline"
                    data-testid={`guide-state-link-${l.key.toLowerCase()}`}
                  >
                    {l.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Related guides */}
        <section className="mt-14" data-testid="guide-related-guides">
          <h2 className="font-display text-xl font-semibold text-slate-900">Keep reading</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            {relatedGuides.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                className="rounded-xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
                data-testid={`guide-related-${g.slug}`}
              >
                <p className="text-xs font-medium uppercase tracking-wider text-green-700">{g.category}</p>
                <p className="mt-2 font-display text-sm font-semibold leading-snug text-slate-900">
                  {g.title}
                </p>
                <p className="mt-2 text-xs text-slate-500">{g.readingMinutes} min read</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Auto-injected "see how your state ranks" cross-link — fires on
            slug families that have a sibling pillar ranking guide. Lets a
            single edit reach all 50 per-state solar-cost guides (and the
            13 per-state renovation cost guides) without touching their
            individual files. */}
        {(() => {
          if (/^solar-panel-cost-[a-z-]+-2026$/.test(guide.slug)) {
            return (
              <div className="mt-10 rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-5" data-testid="auto-xlink-solar-ranking">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">From the pillar series</p>
                <p className="mt-2 font-display text-lg font-semibold text-slate-900">
                  See how your state ranks for 2026 solar payback →
                </p>
                <p className="mt-1 text-sm text-slate-700">
                  All 50 US states ranked by payback years on the same $200/mo baseline. Hawaii 3.1 yr, MA 3.7 yr, CT 4.1 yr down to ND 11.5 yr.
                </p>
                <a
                  href="/guides/solar-panel-roi-by-state-2026"
                  className="mt-3 inline-flex items-center gap-1 rounded-full bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
                  data-testid="auto-xlink-solar-ranking-cta"
                >
                  See the 50-state ranking →
                </a>
              </div>
            );
          }
          return null;
        })()}

        {/* CC-BY-4.0 syndication CTA — bottom of every guide. Editors can
            copy the attribution snippet and republish the full text with a
            follow-link back to the canonical URL. Every republication = one
            branded inbound link. */}
        <SyndicateBlock title={guide.title} slug={guide.slug} />
      </article>

      {/* Sticky "Run the decision math" CTA — only fires on the 5
          ROI/financing/strategy guides where the reader is already
          committed and the next logical step is real-numbers math.
          See STICKY_BANNER_GUIDE_SLUGS in the component for the list.
          Per-slug CTA overrides (e.g. solar guide → solar calc) live
          in STICKY_BANNER_CTA_BY_SLUG. */}
      {STICKY_BANNER_GUIDE_SLUGS.has(guide.slug) && (
        <StickyDecisionCalcBanner guideSlug={guide.slug} {...(STICKY_BANNER_CTA_BY_SLUG[guide.slug] || {})} />
      )}

      <LeadDialog open={leadOpen} onOpenChange={setLeadOpen} source={leadSource || `guide-${guide.slug}`} />
    </>
  );
}

/**
 * Lightweight placeholder shown while the guide's content chunk downloads
 * (pass 92u lazy-body split). The h1, byline, hero, and Article schema all
 * render instantly from metadata — this only covers the article body area.
 */
function GuideBodySkeleton() {
  return (
    <div className="animate-pulse space-y-4 py-2" data-testid="guide-body-skeleton" aria-hidden="true">
      <div className="h-4 w-full rounded bg-slate-100" />
      <div className="h-4 w-11/12 rounded bg-slate-100" />
      <div className="h-4 w-4/5 rounded bg-slate-100" />
      <div className="mt-6 h-6 w-1/2 rounded bg-slate-100" />
      <div className="h-4 w-full rounded bg-slate-100" />
      <div className="h-4 w-10/12 rounded bg-slate-100" />
      <div className="h-4 w-3/4 rounded bg-slate-100" />
      <div className="h-4 w-full rounded bg-slate-100" />
      <div className="h-4 w-2/3 rounded bg-slate-100" />
    </div>
  );
}
